import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { CaretLeft, ShoppingCart, Truck, ArrowLeft, Check } from "@phosphor-icons/react";
import { HighlightBadge } from "@/components/HighlightBadge";
import { PhotoCarousel } from "@/components/PhotoCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_SIZES = ["XS", "S", "M", "L", "XL"];
const CART_KEY = "provertical-cart";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(""); // nombre de la variante elegida
  const [quantity, setQuantity] = useState(1);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products/${id}`)
      .then(r => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    axios.get(`${API}/products`).then(r => setAllProducts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  const addToCart = () => {
    if (!product) return;
    if ((product.sizes || []).length > 0 && !selectedSize) {
      toast.error(lang === "en" ? "Choose a size first" : "Selecciona una talla primero");
      return;
    }
    if ((product.color_variants || []).length > 0 && !selectedColor) {
      toast.error(lang === "en" ? "Choose a color first" : "Selecciona un color primero");
      return;
    }
    // Usar la imagen del color seleccionado como portada del item del carrito
    const variant = (product.color_variants || []).find(v => v.name === selectedColor);
    const itemImage = (variant && (variant.images || [])[0]) || product.image_url;
    const item = { ...product, image_url: itemImage, quantity, selected_size: selectedSize || undefined, selected_color: selectedColor || undefined };
    try {
      const raw = localStorage.getItem(CART_KEY);
      const cart = raw ? JSON.parse(raw) : [];
      const key = `${product.id}::${selectedSize || ""}::${selectedColor || ""}`;
      const idx = cart.findIndex(c => `${c.id}::${c.selected_size || ""}::${c.selected_color || ""}` === key);
      if (idx >= 0) cart[idx].quantity += quantity;
      else cart.push(item);
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {}
    const extra = [selectedSize, selectedColor].filter(Boolean).join(" / ");
    toast.success(`${product.name}${extra ? ` (${extra})` : ""} agregado al carrito`);
  };

  const buyNow = () => {
    if ((product?.sizes || []).length > 0 && !selectedSize) {
      toast.error(lang === "en" ? "Choose a size first" : "Selecciona una talla primero");
      return;
    }
    if ((product?.color_variants || []).length > 0 && !selectedColor) {
      toast.error(lang === "en" ? "Choose a color first" : "Selecciona un color primero");
      return;
    }
    addToCart();
    navigate("/tienda?openCart=1");
  };

  if (loading) return <main className="pt-32 min-h-screen flex items-center justify-center text-gray-400" data-testid="product-detail-loading">{lang === "en" ? "Loading..." : "Cargando..."}</main>;
  if (!product) return (
    <main className="pt-32 min-h-screen flex flex-col items-center justify-center text-gray-400 px-6" data-testid="product-detail-not-found">
      <p className="text-2xl font-bold mb-2">{lang === "en" ? "Product not found" : "Producto no encontrado"}</p>
      <Link to="/tienda"><Button variant="outline" className="border-white/10 mt-4"><ArrowLeft size={16} className="mr-2" /> {lang === "en" ? "Back to store" : "Volver a la tienda"}</Button></Link>
    </main>
  );

  const variants = product.color_variants || [];
  const activeVariant = variants.find(v => v.name === selectedColor);
  const allPhotos = activeVariant && (activeVariant.images || []).length > 0
    ? activeVariant.images
    : [product.image_url, ...(product.images || [])].filter(Boolean);
  const availableSizes = (product.sizes || []).filter(s => ALL_SIZES.includes(s));
  const hasSpecs = (product.specs || []).length > 0;
  const hasFeatures = (product.features || []).length > 0;
  const hasFullDesc = (product.full_description || "").trim().length > 0;

  return (
    <main className="pt-20 min-h-screen pb-24" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <Link to="/tienda" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#FF4D00] mb-6 transition-colors" data-testid="back-to-store">
          <CaretLeft size={16} weight="bold" /> {lang === "en" ? "Back to store" : "Volver a la tienda"}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Galeria */}
          <div data-testid="product-gallery" className="relative">
            <PhotoCarousel photos={allPhotos} aspectClass="aspect-square" altPrefix={product.name} />
            <HighlightBadge text={product.badge_text} color={product.badge_color} className="top-3 left-3 text-xs sm:text-sm px-3 py-1 sm:py-1.5" />
          </div>

          {/* Info producto */}
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-gray-400 border-white/10 capitalize">{product.category}</Badge>
              {product.gender && <Badge variant="outline" className="text-blue-300 border-blue-300/20 capitalize" data-testid="product-gender-badge">{product.gender}</Badge>}
              {product.featured && <Badge className="bg-[#FF4D00]/20 text-[#FF4D00]">{t("featured")}</Badge>}
              {product.is_dropshipping && product.shipping_days && (
                <Badge variant="outline" className="text-blue-400 border-blue-400/30 flex items-center gap-1"><Truck size={12} weight="bold" /> {product.shipping_days}{product.shipping_origin ? ` - ${product.shipping_origin}` : ""}</Badge>
              )}
            </div>

            <div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight font-['Cabinet_Grotesk']" data-testid="product-name">{product.name}</h1>
              <p className="text-3xl text-[#FF4D00] font-bold mt-3" data-testid="product-price">{formatPrice(product.price)}</p>
              <p className="text-xs text-gray-500 mt-1" data-testid="product-stock">{product.stock > 0 ? (lang === "en" ? "Available" : "Disponible") : t("no_stock")}</p>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap" data-testid="product-short-desc">{product.description}</p>

            {/* Selector de color/diseño */}
            {variants.length > 0 && (
              <div data-testid="product-color-selector">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400 mb-3">
                  {lang === "en" ? "Color" : "Color"}
                  {selectedColor && <span className="text-[#FF4D00] ml-2 normal-case font-bold tracking-normal">{selectedColor}</span>}
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {variants.map((v, idx) => {
                    const active = selectedColor === v.name;
                    const outOfStock = v.stock !== undefined && Number(v.stock) <= 0;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => { if (!outOfStock) setSelectedColor(v.name); }}
                        disabled={outOfStock}
                        title={outOfStock ? (lang === "en" ? "Out of stock" : "Agotado") : (v.name || `Variante ${idx + 1}`)}
                        className={`group relative flex items-center gap-2 pl-1 pr-3 h-12 border-2 transition-all ${
                          outOfStock
                            ? "border-white/10 text-gray-600 cursor-not-allowed bg-white/[0.02] line-through"
                            : active
                              ? "border-[#FF4D00] bg-white/[0.03]"
                              : "border-white/15 hover:border-white/40"
                        }`}
                        data-testid={`color-${idx}`}
                      >
                        {v.images && v.images[0] ? (
                          <img src={v.images[0]} alt={v.name} className={`w-9 h-9 object-cover ${outOfStock ? "opacity-40 grayscale" : ""}`} />
                        ) : (
                          <span className={`w-9 h-9 inline-block ${outOfStock ? "opacity-40" : ""}`} style={{ backgroundColor: v.hex || "#000" }} />
                        )}
                        <span className={`text-xs font-bold ${active && !outOfStock ? "text-white" : "text-gray-300"}`}>{v.name || `#${idx + 1}`}</span>
                        {outOfStock && (
                          <span className="absolute -top-2 -right-2 bg-red-500/90 text-white text-[8px] px-1.5 py-0.5 uppercase tracking-wider font-bold rounded">
                            {lang === "en" ? "Out" : "Agot."}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selector de talla */}
            {availableSizes.length > 0 && (
              <div data-testid="product-size-selector">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400 mb-3">{lang === "en" ? "Size" : "Talla"} {selectedSize && <span className="text-[#FF4D00] ml-2">{selectedSize}</span>}</p>
                <div className="flex flex-wrap gap-2">
                  {ALL_SIZES.map(s => {
                    const exists = availableSizes.includes(s);
                    const active = selectedSize === s;
                    if (!exists) return null;
                    const sbs = product.stock_by_size || {};
                    const sizeStock = sbs[s];
                    // Si el producto define stock por talla, una talla con 0 está agotada.
                    // Si no hay stock_by_size definido para esta talla, se considera disponible.
                    const outOfStock = sizeStock !== undefined && Number(sizeStock) <= 0;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { if (!outOfStock) setSelectedSize(s); }}
                        disabled={outOfStock}
                        title={outOfStock ? (lang === "en" ? "Out of stock" : "Agotado") : ""}
                        className={`relative min-w-[3rem] h-12 px-4 border-2 font-bold text-sm transition-all ${
                          outOfStock
                            ? "border-white/10 text-gray-600 line-through cursor-not-allowed bg-white/[0.02]"
                            : active
                              ? "bg-[#FF4D00] border-[#FF4D00] text-white"
                              : "border-white/15 text-gray-300 hover:border-white/40"
                        }`}
                        data-testid={`size-${s.toLowerCase()}`}
                      >
                        {s}
                        {outOfStock && (
                          <span className="absolute -top-2 -right-2 bg-red-500/90 text-white text-[8px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">
                            {lang === "en" ? "Out" : "Agot."}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cantidad */}
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400 mb-3">{lang === "en" ? "Quantity" : "Cantidad"}</p>
              <div className="inline-flex border border-white/15">
                <button type="button" className="w-10 h-12 hover:bg-white/5 disabled:opacity-30" disabled={quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))} data-testid="qty-minus">−</button>
                <span className="w-14 h-12 flex items-center justify-center font-bold" data-testid="qty-value">{quantity}</span>
                <button type="button" className="w-10 h-12 hover:bg-white/5 disabled:opacity-30" disabled={quantity >= product.stock} onClick={() => setQuantity(q => Math.min(product.stock, q + 1))} data-testid="qty-plus">+</button>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold flex-1 min-w-[200px] h-14" disabled={product.stock <= 0} onClick={buyNow} data-testid="product-buy-now">
                {product.stock <= 0 ? t("sold_out") : (lang === "en" ? "Buy now" : "Comprar ahora")}
              </Button>
              <Button size="lg" variant="outline" className="border-white/15 text-white h-14 flex-1 min-w-[200px]" disabled={product.stock <= 0} onClick={addToCart} data-testid="product-add-cart">
                <ShoppingCart size={18} className="mr-2" /> {t("add_to_cart")}
              </Button>
            </div>

            {hasFeatures && (
              <div className="pt-4 border-t border-white/5" data-testid="product-features">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400 mb-3">{lang === "en" ? "Features" : "Caracteristicas"}</p>
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300"><Check size={16} weight="bold" className="text-[#FF4D00] mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </motion.div>

        {/* Descripcion larga + Especificaciones (full width) */}
        {(hasFullDesc || hasSpecs) && (
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-7xl">
            {hasFullDesc && (
              <div data-testid="product-full-desc">
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-4">{lang === "en" ? "Description" : "Descripcion"}</h2>
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{product.full_description}</p>
                </div>
              </div>
            )}
            {hasSpecs && (
              <div data-testid="product-specs">
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-4">{lang === "en" ? "Specifications" : "Especificaciones"}</h2>
                <dl className="border-t border-white/10">
                  {product.specs.map((spec, i) => (
                    <div key={i} className="grid grid-cols-2 border-b border-white/10 py-3 gap-4">
                      <dt className="text-sm text-gray-400">{spec.label}</dt>
                      <dd className="text-sm text-white">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        )}

        {/* ═══ PRODUCTOS RELACIONADOS / SUGERENCIAS ═══ */}
        <RelatedProducts current={product} all={allProducts} lang={lang} formatPrice={formatPrice} />
      </div>
    </main>
  );
}

function RelatedProducts({ current, all, lang, formatPrice }) {
  const scrollRef = useRef(null);
  if (!current || !all || all.length === 0) return null;
  const others = all.filter(p => p.id !== current.id);
  const sameCat = others.filter(p => p.category === current.category);
  const offers = others.filter(p => p.badge_text && p.category !== current.category);
  const seen = new Set();
  const items = [...sameCat, ...offers, ...others].filter(p => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
  if (items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <section className="mt-20 pt-12 border-t border-white/10" data-testid="related-products">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4D00] mb-1">{lang === "en" ? "You may also like" : "Tambien te puede gustar"}</p>
          <h2 className="text-2xl sm:text-3xl font-black uppercase font-['Cabinet_Grotesk'] tracking-tight">{lang === "en" ? "Similar products" : "Productos similares"}</h2>
        </div>
        <div className="flex items-center gap-4">
          {items.length > 4 && (
            <div className="hidden sm:flex gap-2">
              <button onClick={() => scrollBy(-1)} className="w-9 h-9 border border-white/15 hover:border-white/40 text-gray-300 hover:text-white transition-colors inline-flex items-center justify-center" aria-label="Anterior" data-testid="related-prev">
                <CaretLeft size={16} weight="bold" />
              </button>
              <button onClick={() => scrollBy(1)} className="w-9 h-9 border border-white/15 hover:border-white/40 text-gray-300 hover:text-white transition-colors inline-flex items-center justify-center" aria-label="Siguiente" data-testid="related-next">
                <CaretLeft size={16} weight="bold" className="rotate-180" />
              </button>
            </div>
          )}
          <Link to="/tienda" className="text-xs text-gray-400 hover:text-[#FF4D00] uppercase tracking-wider font-bold inline-flex items-center gap-1">
            {lang === "en" ? "View all" : "Ver toda la tienda"} →
          </Link>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-3 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-6 lg:-mx-8 px-6 lg:px-8"
        style={{ scrollbarWidth: "none" }}
        data-testid="related-scroller"
      >
        {items.map(p => (
          <Link
            key={p.id}
            to={`/tienda/producto/${p.id}`}
            className="bg-[#121212] border border-white/5 overflow-hidden flex flex-col card-hover group snap-start shrink-0 w-[46%] sm:w-[31%] lg:w-[23%]"
            data-testid={`related-product-${p.id}`}
          >
            <div className="aspect-square bg-[#1A1A1A] relative overflow-hidden">
              <img src={p.image_url || (p.images || [])[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <HighlightBadge text={p.badge_text} color={p.badge_color} />
            </div>
            <div className="p-3 flex-1 flex flex-col">
              <Badge variant="outline" className="self-start text-[9px] sm:text-[10px] text-gray-400 border-white/10 mb-1.5">{p.category}</Badge>
              <h3 className="text-xs sm:text-sm font-semibold font-['Cabinet_Grotesk'] line-clamp-2 group-hover:text-[#FF4D00] transition-colors">{p.name}</h3>
              <p className="mt-auto pt-2 text-sm sm:text-base font-bold text-[#FF4D00]">{formatPrice(p.price)}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
